import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { api, API, getToken, formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { toast } from "sonner";
import { Mail, Eye } from "lucide-react";

const ROLE_STYLES = {
  client:      { bg: "bg-[#FFF7E6]", border: "border-[#FDE68A]", text: "text-[#92400E]", label: "Client" },
  commercant:  { bg: "bg-[#E8F7EE]", border: "border-[#BFE5CC]", text: "text-[#1F7A3D]", label: "Commerçant" },
  licencie:    { bg: "bg-[#E5EEFF]", border: "border-[#BFD3FA]", text: "text-[#1B4FA6]", label: "Licencié" },
};

const ROLE_FILTERS = [
  { id: "all",        label: "Tous" },
  { id: "client",     label: "Que reçoit le client" },
  { id: "commercant", label: "Que reçoit le commerçant" },
  { id: "licencie",   label: "Que reçoit le licencié" },
];

export function EmailPreviewPanel() {
  const [templates, setTemplates] = useState([]);
  const [filter, setFilter] = useState("all");
  const [previewTpl, setPreviewTpl] = useState(null);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  useEffect(() => {
    api.get("/email-templates").then(({ data }) => setTemplates(data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  const filtered = useMemo(
    () => (filter === "all" ? templates : templates.filter((t) => t.recipient_role === filter)),
    [templates, filter]
  );

  const openPreview = async (tpl) => {
    setPreviewTpl(tpl);
    setPreviewHtml(null);
    setLoadingPreview(true);
    try {
      const res = await axios.get(`${API}/email-templates/${tpl.id}/preview`, {
        headers: { Authorization: `Bearer ${getToken()}` },
        responseType: "text",
      });
      setPreviewHtml(res.data);
    } catch {
      toast.error("Aperçu indisponible");
      setPreviewTpl(null);
    } finally {
      setLoadingPreview(false);
    }
  };

  const closePreview = () => { setPreviewTpl(null); setPreviewHtml(null); };

  return (
    <div className="bg-white rounded-2xl border border-[#E5E7EB] shadow-[0_8px_24px_rgba(57,125,255,0.06)] p-6 sm:p-7" data-testid="email-preview-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap mb-5">
        <div>
          <h2 className="font-display text-2xl font-bold text-[#191D1E] tracking-tight">Aperçu des emails du programme</h2>
          <p className="text-sm text-[#687175] mt-1 max-w-2xl">
            Tous les emails déclenchés par la plateforme Place des Énergies. Transparence totale : chaque rôle peut voir ce que les autres reçoivent.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-6" data-testid="email-filters">
        {ROLE_FILTERS.map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            data-testid={`email-filter-${f.id}`}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors border ${
              filter === f.id
                ? "bg-[#191D1E] text-white border-[#191D1E]"
                : "bg-white text-[#191D1E] border-[#E5E7EB] hover:border-[#191D1E]"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="email-cards">
        {filtered.map((t) => {
          const style = ROLE_STYLES[t.recipient_role] || ROLE_STYLES.client;
          return (
            <div
              key={t.id}
              className="rounded-2xl border border-[#E5E7EB] overflow-hidden flex flex-col"
              data-testid={`email-card-${t.id}`}
            >
              <div className={`${style.bg} ${style.border} border-b px-4 py-3 flex items-center gap-2`}>
                <Mail className={`w-4 h-4 ${style.text}`} />
                <span className={`text-xs font-bold uppercase tracking-wider ${style.text}`}>
                  Destinataire : {style.label}
                </span>
              </div>
              <div className="p-5 flex-1 flex flex-col">
                <h3 className="font-display text-lg font-bold text-[#191D1E] leading-tight">{t.label}</h3>
                <p className="text-xs text-[#687175] mt-2 leading-relaxed">
                  <span className="font-semibold text-[#191D1E]">Déclenché&nbsp;:</span> {t.trigger}
                </p>
                <p className="text-xs text-[#191D1E] mt-3 font-mono bg-[#F5F6F9] rounded px-2 py-1.5 truncate" title={t.subject}>
                  <span className="text-[#687175]">Objet :</span> {t.subject}
                </p>
                <Button
                  onClick={() => openPreview(t)}
                  data-testid={`email-preview-btn-${t.id}`}
                  className="mt-4 bg-[#F9C80E] hover:bg-[#DDB10A] text-[#191D1E] font-semibold rounded-xl"
                >
                  <Eye className="w-4 h-4 mr-1.5" /> Aperçu de l'email
                </Button>
              </div>
            </div>
          );
        })}
      </div>

      <Dialog open={!!previewTpl} onOpenChange={(v) => !v && closePreview()}>
        <DialogContent className="max-w-4xl w-[95vw] h-[90vh] p-0 flex flex-col overflow-hidden" data-testid="email-preview-dialog">
          <DialogHeader className="px-6 pt-5 pb-3 border-b border-[#E5E7EB]">
            <DialogTitle className="font-display text-xl">{previewTpl?.label}</DialogTitle>
            <DialogDescription className="font-mono text-xs">{previewTpl?.subject}</DialogDescription>
          </DialogHeader>
          <div className="flex-1 bg-[#F5F6F9]" data-testid="email-preview-iframe-wrap">
            {loadingPreview || !previewHtml ? (
              <div className="h-full flex items-center justify-center text-sm text-[#687175]">
                Chargement de l'aperçu…
              </div>
            ) : (
              <iframe
                title="email-preview"
                srcDoc={previewHtml}
                className="w-full h-full border-0 bg-white"
                data-testid="email-preview-iframe"
                sandbox="allow-same-origin"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default EmailPreviewPanel;
